/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// reactstrap components
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, Container, Row, Table } from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import ConfirmationModal from '../components/Misc/ConfirmationModal.js';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// core components
import Header from 'components/Headers/Header';
import { useSortableData } from 'components/Misc/HeaderSorting';
import PatientRowName from 'components/Misc/PatientRowName';
import PatientModal from 'components/Modals/PatientModal.js';
import { useSelectAll } from '../hooks/useSelectAll';

const Patients = () => {
  const navigate = useNavigate();

  // Get our Globals
  const { apiClient } = useContext(GlobalContext);

  // Update the user patients and conversations the first time this component loads
  const [patients, setPatients] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]); // State to track selected patients
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false); // State for delete patient confirmation modal
  const [deletePatientID, setDeletePatientID] = useState(-1);
  const [editPatientID, setEditPatientID] = useState(-1);
  const [isPatientModalOpen, setPatientModelOpen] = useState(false);

  // Use the sortable data hook
  const {
    sortedItems: sortedPatients,
    sortConfig,
    handleSort,
  } = useSortableData(
    patients,
    'lastName', // Default sort key
    'asc', // Default sort direction
  );

  // Use the custom hook for "Select All" functionality
  const {
    selectAll: selectAllPatients,
    handleSelectAll: handleSelectAllPatients,
  } = useSelectAll(
    sortedPatients,
    'uid',
    selectedPatients,
    setSelectedPatients,
  );

  useEffect(() => {
    // Go get the conversations for this user
    apiClient
      .updatePatients()
      .then((data) => {
        setPatients(apiClient.patients);
      })
      .catch((error) => {
        // probably no session key
        // console.log(error);
        console.log('Did not get conversations');
      });
  }, [apiClient]);

  function lookupPatient(uid) {
    return apiClient.patients.filter((patient) => patient.uid === uid)[0];
  }

  async function editPatient(patientUid, fName, lName, dob) {
    // Do the API Call to edit the patient
    // Check what fields of the patient changed and only send those fields to the API
    var oldPatient = lookupPatient(patientUid);
    const oldFName = oldPatient.firstName;
    const oldLName = oldPatient.lastName;
    const oldDob = oldPatient.dob;

    // Update the patient name in the existing apiClient.patients list
    for (var i = 0; i < apiClient.patients.length; i++) {
      if (apiClient.patients[i].uid === patientUid) {
        apiClient.patients[i].firstName = fName;
        apiClient.patients[i].lastName = lName;
        // Need to condition the date of birth to be in the format MM/DD/YYYY
        // console.log(dob)
        dob = new Date(dob + 'T00:00:00');
        // console.log(dob)
        // console.log(dob.toLocaleDateString())
        apiClient.patients[i].dob = dob.toLocaleDateString();
      }
    }

    // Update patient info on the page
    setPatients(apiClient.patients);

    // Update the patient info in the database
    if (oldFName !== fName) {
      // console.log("Updating patient first name")
      await apiClient.editPatient(patientUid, 'firstName', fName);
    }
    if (oldLName !== lName) {
      // console.log("Updating patient last name")
      await apiClient.editPatient(patientUid, 'lastName', lName);
    }
    if (oldDob !== dob.toLocaleDateString()) {
      // console.log("Updating patient dob")
      await apiClient.editPatient(patientUid, 'dob', dob);
    }
  }

  function deletePatient(patientId) {
    // Remove patient from the list of patients
    setPatients((prevPatients) =>
      prevPatients.filter((patient) => patient.uid !== patientId),
    );

    // if the patient is in the apiClient.patients list then remove it
    apiClient.patients = apiClient.patients.filter(
      (patient) => patient.uid !== patientId,
    );

    // Do the API Call to delete the patient
    apiClient
      .deletePatient(patientId)
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        // probably no session key
        console.log(error);
        console.log('Did not delete patient');
      });
  }

  function handlePatientSelection(event, patientId) {
    if (event.target.checked) {
      // Add patientId to the array
      setSelectedPatients([...selectedPatients, patientId]);
    } else {
      // Remove patientId from the array
      setSelectedPatients(selectedPatients.filter((id) => id !== patientId));
    }
  }

  function handleEditClick() {
    if (selectedPatients.length === 1) {
      setEditPatientID(selectedPatients[0]);
      setPatientModelOpen(true);
    } else {
      // Show a toast or alert to inform user they can only edit one patient at a time
      toast.info('Please select only one patient to edit');
    }
  }

  return (
    <>
      <Header isLite={true} />
      {/* Delete patient modal */}
      <ConfirmationModal
        isOpen={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        onConfirm={() => {
          // Delete each selected patient
          selectedPatients.forEach((patientId) => {
            deletePatient(patientId);
          });
          // Clear the selected list
          setSelectedPatients([]);
          // Close the modal
          setDeleteConfirmationOpen(false);
        }}
      >
        Are you sure you want to delete {selectedPatients.length} patient(s) and
        all of their conversations?
      </ConfirmationModal>

      {/* Edit Patient Info modal */}
      <PatientModal
        isOpen={isPatientModalOpen}
        onClose={() => setPatientModelOpen(false)}
        onSave={(fName, lName, dob) =>
          editPatient(editPatientID, fName, lName, dob)
        }
        isEditMode={true}
        patient={lookupPatient(editPatientID)}
      ></PatientModal>

      {/* Page content */}
      <Container className="mt--7" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 header-flex">
                <h3 className="mb-0">Patients</h3>
              </CardHeader>
              <div
                className="tableFixHead table-responsive"
                style={{ maxHeight: '900px', overflowY: 'auto' }}
              >
                <Table className="align-items-center table-flush">
                  <thead className="thead-light">
                    <tr>
                      <th
                        scope="col"
                        style={{
                          paddingLeft: '20px',
                          cursor:
                            selectedPatients.length > 0
                              ? 'pointer'
                              : 'not-allowed',
                          color: selectedPatients.length > 0 ? '#000' : '#aaa',
                        }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectAllPatients}
                            onChange={handleSelectAllPatients}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '16px',
                              height: '16px',
                              marginLeft: '9px',
                              marginRight: '9px',
                            }}
                          />
                          <i
                            className="fa fa-trash"
                            aria-hidden="true"
                            style={{
                              fontSize: '1.2rem',
                              cursor: 'pointer',
                              color:
                                selectedPatients.length > 0 ? '#000' : '#aaa',
                            }}
                            onClick={() => {
                              if (selectedPatients.length > 0) {
                                setDeleteConfirmationOpen(true);
                              } else {
                                toast.info(
                                  'Please select at least one patient to delete.',
                                );
                              }
                            }}
                          />
                          <i
                            className="fa fa-pencil"
                            aria-hidden="true"
                            style={{
                              fontSize: '1.2rem',
                              cursor: 'pointer',
                              color:
                                selectedPatients.length > 0 ? '#000' : '#aaa',
                            }}
                            onClick={handleEditClick}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort('lastName')}
                        style={{ cursor: 'pointer' }}
                      >
                        Lastname, Firstname{' '}
                        {sortConfig.key === 'lastName'
                          ? sortConfig.direction === 'asc'
                            ? '▲'
                            : '▼'
                          : ''}
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort('conversationIds')}
                        style={{ cursor: 'pointer' }}
                      >
                        Visits{' '}
                        {sortConfig.key === 'conversationIds'
                          ? sortConfig.direction === 'asc'
                            ? '▲'
                            : '▼'
                          : ''}
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort('lastUsed')}
                        style={{ cursor: 'pointer' }}
                      >
                        Last Used{' '}
                        {sortConfig.key === 'lastUsed'
                          ? sortConfig.direction === 'asc'
                            ? '▲'
                            : '▼'
                          : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPatients.map((patient) => (
                      <tr
                        key={patient.uid}
                        onClick={() =>
                          navigate(`/User/conversations#${patient.uid}`)
                        }
                        className="clickable-row"
                      >
                        <td>
                          <div
                            style={{
                              display: 'inline-block',
                              paddingLeft: '8px',
                              padding: '5px',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPatients.includes(patient.uid)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                handlePatientSelection(e, patient.uid)
                              }
                              style={{
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          </div>
                        </td>
                        <PatientRowName
                          fName={patient.firstName}
                          lName={patient.lastName}
                          dob={patient.dob}
                        />
                        <td>
                          {patient.conversationIds
                            ? patient.conversationIds.length
                            : null}
                        </td>
                        <td>{patient.lastUsed}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>
          </div>
        </Row>
      </Container>
      <ToastContainer />
    </>
  );
};

export default Patients;
